/**
 * GolemSkillEffect — Grit ironbody-path sub-stance: HP regeneration toggle.
 *
 * A sub-stance inside Ironbody. While Ironbody is active, the player can
 * toggle Golem on to regenerate 2% of max HP per second. Golem auto-drops
 * when Ironbody drops. No additional DR or combat bonuses — purely HP
 * recovery that rewards staying in the shell.
 *
 * Supports upgrade keys:
 * - Deep Roots: `regenPerSecond` (additive, +0.005 → 2.5% total)
 *
 * Extends ToggleSkillEffect for active/cooldown lifecycle.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see grit-spec.md § Golem
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface GolemDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly ironbody: {
    isActive(): boolean;
  };
  readonly player: {
    readonly maxHp: number;
    heal(amount: number): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface GolemConfig {
  readonly cooldownMs: number;
  readonly baseRegenRate: number;
}

const DEFAULT_CONFIG: GolemConfig = {
  cooldownMs: 2000,         // long cooldown tier
  baseRegenRate: 0.02,      // 2% max HP per second
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class GolemSkillEffect extends ToggleSkillEffect {
  readonly skillId = 'golem';

  private readonly deps: GolemDeps;
  private readonly config: GolemConfig;

  // Resolved values
  private _resolvedRegenRate = 0;
  private _totalHealed = 0;

  constructor(deps: GolemDeps, config: Partial<GolemConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get resolvedRegenRate(): number { return this._resolvedRegenRate; }
  get totalHealed(): number { return this._totalHealed; }

  // --- ToggleSkillEffect interface ---

  canActivate(): boolean {
    return !this._isActive
      && this.cooldownRemaining <= 0
      && this.deps.ironbody.isActive();
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._isActive = true;
    this._totalHealed = 0;

    // Resolve regen rate (base + Deep Roots bonus)
    const bonusRegen = this.deps.resolver.getAdditiveEffect('golem', 'regenPerSecond');
    this._resolvedRegenRate = this.config.baseRegenRate + bonusRegen;

    return true;
  }

  protected onDeactivate(): void {
    this.cooldownRemaining = this.config.cooldownMs;
  }

  protected updateWhileActive(delta: number): void {
    // Auto-drop when Ironbody falls
    if (!this.deps.ironbody.isActive()) {
      this.deactivate();
      return;
    }

    // Apply HP regeneration: maxHp × regenRate × (delta / 1000)
    const healAmount = this.deps.player.maxHp * this._resolvedRegenRate * (delta / 1000);
    if (healAmount > 0) {
      this.deps.player.heal(healAmount);
      this._totalHealed += healAmount;
    }
  }
}
